import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

const SIDE_IMG =
  "https://images.unsplash.com/photo-1565598621680-94ac0c22b148?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwyfHxlbGVnYW50JTIwc3R1ZGVudCUyMGxlYXJuaW5nJTIwY29tcHV0ZXIlMjBjb3NtZXRpY3N8ZW58MHx8fHwxNzg2NjU2MjA0fDA&ixlib=rb-4.1.0&q=85";

const LEVELS = ["BTS", "CAP", "BP"];

export const AuthShell = ({ title, subtitle, children }) => (
  <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
    <div className="relative hidden lg:block">
      <img src={SIDE_IMG} alt="Étudiante" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#2C2627]/80 via-[#2C2627]/20 to-transparent" />
      <div className="relative h-full flex flex-col justify-between p-12">
        <Link to="/" className="font-logo text-3xl text-white">Wonderpro</Link>
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-white/70 mb-4">Esthétique &amp; Cosmétique</p>
          <p className="font-display text-3xl text-white leading-tight max-w-sm">
            Vos supports de cours BTS, CAP et BP, réunis en un seul endroit.
          </p>
        </div>
      </div>
    </div>

    <div className="flex items-center px-6 sm:px-12 lg:px-20 py-16 bg-[#FDFBF7]">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <Link to="/" className="lg:hidden font-logo text-2xl text-[#B76E79] block mb-10">Wonderpro</Link>
        <h1 className="font-display text-4xl lg:text-5xl tracking-tight text-[#2C2627] mb-3">{title}</h1>
        <p className="text-sm text-[#5E5356] mb-10 leading-relaxed">{subtitle}</p>
        {children}
      </motion.div>
    </div>
  </div>
);

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", password: "", level: "BTS" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(form);
      toast.success("Inscription réussie. Bienvenue !");
      const q = params.get("q");
      navigate(q ? `/ressources?q=${encodeURIComponent(q)}` : "/ressources");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const field = (k) => ({
    value: form[k],
    onChange: (e) => setForm({ ...form, [k]: e.target.value }),
  });

  return (
    <AuthShell
      title="Inscription étudiant"
      subtitle="Créez votre compte pour accéder aux pages internes et télécharger les ressources."
    >
      <form onSubmit={submit} className="space-y-6" data-testid="register-form">
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-[0.16em] text-[#5E5356]">Nom complet</Label>
          <Input
            data-testid="register-name-input"
            required
            {...field("name")}
            placeholder="Camille Durand"
            className="h-12 rounded-xl border-[#E8D7D0] focus-visible:ring-[#B76E79]"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-[0.16em] text-[#5E5356]">Email</Label>
          <Input
            data-testid="register-email-input"
            type="email"
            required
            {...field("email")}
            placeholder="camille@exemple.fr"
            className="h-12 rounded-xl border-[#E8D7D0] focus-visible:ring-[#B76E79]"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-[0.16em] text-[#5E5356]">Mot de passe</Label>
          <Input
            data-testid="register-password-input"
            type="password"
            required
            minLength={6}
            {...field("password")}
            placeholder="6 caractères minimum"
            className="h-12 rounded-xl border-[#E8D7D0] focus-visible:ring-[#B76E79]"
          />
        </div>
        <div className="space-y-3">
          <Label className="text-xs uppercase tracking-[0.16em] text-[#5E5356]">Filière</Label>
          <div className="flex gap-3">
            {LEVELS.map((l) => (
              <button
                key={l}
                type="button"
                data-testid={`register-level-${l}`}
                onClick={() => setForm({ ...form, level: l })}
                className={`flex-1 h-11 rounded-full text-sm font-medium border transition-colors duration-300 ${
                  form.level === l
                    ? "bg-[#B76E79] text-white border-[#B76E79]"
                    : "bg-white text-[#5E5356] border-[#E8D7D0] hover:bg-[#F5E6E8]"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <p data-testid="register-error" className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-3">
            {error}
          </p>
        )}

        <Button
          type="submit"
          disabled={loading}
          data-testid="register-submit-btn"
          className="w-full h-12 rounded-full bg-[#B76E79] hover:bg-[#A25D67] text-white text-base"
        >
          {loading ? "Création…" : "Créer mon compte"}
        </Button>

        <p className="text-sm text-[#5E5356]">
          Déjà inscrit ?{" "}
          <Link to="/connexion" data-testid="register-to-login-link" className="text-[#B76E79] hover:underline">
            Se connecter
          </Link>
        </p>
      </form>
    </AuthShell>
  );
}
